import { Vehicle } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { type FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Issue } from 'validata';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import { formikCheck } from '../../utils/validation.js';
import type { VehicleFormValues } from './form.view.js';
import { VehicleFormView } from './form.view.js';

interface VehicleFormProps {
  initialValues?: VehicleFormValues;
  isEditing?: boolean;
  onCancel: () => void;
  onSave: (
    values: VehicleFormValues & { originalRego?: string },
  ) => Promise<void>;
}

export const VehicleForm: FC<VehicleFormProps> = ({
  onCancel,
  onSave,
  initialValues,
  isEditing,
}) => {
  const contractToUse = isEditing
    ? Vehicle.patchContract
    : Vehicle.createContract;
  const formik = useFormik<VehicleFormValues>({
    initialValues: initialValues || {
      color: '',
      hasLivery: false,
      make: '',
      model: '',
      rego: '',
    },
    onSubmit: async (values) => {
      try {
        await onSave({
          ...values,
          originalRego: initialValues?.rego,
        });
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    validate: (value) => formikCheck(contractToUse, value),
  });

  return (
    <VehicleFormView cancel={onCancel} formik={formik} isEditing={isEditing} />
  );
};

export const CreateVehicleContent: FC = () => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useSetTitle('Add Vehicle');

  const handleSave = async (
    values: VehicleFormValues & { originalRego?: string },
  ) => {
    try {
      await auth.axios.post('/api/v1/my/organization/vehicles', {
        color: values.color,
        hasLivery: values.hasLivery,
        make: values.make,
        model: values.model,
        rego: values.rego,
      });
      enqueueSnackbar('Vehicle added', { variant: 'success' });
      navigate('/vehicles');
    } catch (error: unknown) {
      if (error instanceof Error) {
        const axiosError = error as AxiosError<{ issues: Issue[] }>;
        if (
          axiosError.response?.status === 400 &&
          'issues' in axiosError.response.data
        ) {
          enqueueSnackbar('Unable to add vehicle', { variant: 'error' });
          return;
        }
      }
      enqueueSnackbar('Error adding vehicle', { variant: 'error' });
      Sentry.captureException(error);
    }
  };

  return (
    <VehicleForm onCancel={() => navigate('/vehicles')} onSave={handleSave} />
  );
};

export const EditVehicleContent: FC = () => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const { rego } = useParams<{ rego: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [initialValues, setInitialValues] = useState<VehicleFormValues>();
  const [loading, setLoading] = useState(true);

  useSetTitle('Edit Vehicle');

  useEffect(() => {
    if (!rego) return;
    auth.axios
      .get(`/api/v1/my/organization/vehicles/${encodeURIComponent(rego)}`)
      .then(({ data }: { data: Vehicle.Type }) => {
        setInitialValues({
          color: data.color,
          hasLivery: data.hasLivery,
          make: data.make,
          model: data.model,
          rego: data.rego,
        });
      })
      .catch((error) => {
        enqueueSnackbar('Error loading vehicle', { variant: 'error' });
        Sentry.captureException(error);
      })
      .finally(() => setLoading(false));
  }, [rego, auth, enqueueSnackbar]);

  const handleSave = async (
    values: VehicleFormValues & { originalRego?: string },
  ) => {
    if (!rego) return;
    try {
      await auth.axios.patch(
        `/api/v1/my/organization/vehicles/${encodeURIComponent(rego)}`,
        {
          color: values.color,
          hasLivery: values.hasLivery,
          make: values.make,
          model: values.model,
        },
      );
      enqueueSnackbar('Vehicle updated', { variant: 'success' });
      navigate('/vehicles');
    } catch (error) {
      enqueueSnackbar('Error updating vehicle', { variant: 'error' });
      Sentry.captureException(error);
    }
  };

  if (loading || !initialValues) {
    return <div>Loading...</div>;
  }

  return (
    <VehicleForm
      initialValues={initialValues}
      isEditing={true}
      onCancel={() => navigate('/vehicles')}
      onSave={handleSave}
    />
  );
};
